import { cycleDirection, directionDelta, directionFromVector } from "../direction";
import { Mob } from "../mob";
import { PhysicalThingWithHp } from "../physicalThingWithHp";
import { randomChance, randomInt } from "../random";
import type { MiningRobot } from "../things/miningRobot";
import { XY } from "../xy";
import { BasicAI } from "./basicAI";
import { isBreakableObstacle } from "./breakingTier";

export class MiningRobotAI extends BasicAI {
	private miningRobotActor: MiningRobot;

	constructor(actor: MiningRobot) {
		super(actor);
		this.miningRobotActor = actor;
	}

	protected override breakSomethingTowardsTarget(target: PhysicalThingWithHp): boolean {
		if (!randomChance(this.probabilityToBreakBlockWhenStuck)) return false;

		const targetXY = target.xy;
		const deltax = targetXY.x - this.actor.getXY().x;
		const deltay = targetXY.y - this.actor.getXY().y;
		const vector = new XY(Math.sign(deltax), Math.sign(deltay));

		let direction = directionFromVector(vector);
		if (!direction) return false;
		if (randomChance(0.2)) direction = cycleDirection(direction, randomInt(-1, 1));

		const controller = this.miningRobotActor.controller;
		if (!controller) return false;

		const currentArea = new Set<string>();
		for (const part of controller.getParts()) {
			currentArea.add(`${part.xy.x},${part.xy.y}`);
		}
		currentArea.add(`${this.miningRobotActor.xy.x},${this.miningRobotActor.xy.y}`);

		const delta = directionDelta(direction);

		const newPositions: XY[] = [];
		for (const part of controller.getParts()) {
			newPositions.push(part.xy.add(delta));
		}

		const allViable = newPositions.every((p) => {
			if (currentArea.has(`${p.x},${p.y}`)) return true;
			if (!this.getBoard().isInBounds(p)) return false;
			if (this.getBoard().isPassable(p)) return true;
			if (this.isAreaDiggable(p)) return true;
			return false;
		});

		if (!allViable) return false;

		const attackPositions = newPositions.filter((p) => !currentArea.has(`${p.x},${p.y}`));

		const targets = new Set<PhysicalThingWithHp>();
		for (const xy of attackPositions) {
			for (const thing of this.getBoard().getThingsAt(xy)) {
				if (thing instanceof PhysicalThingWithHp && this.isDamageableObstacle(thing)) {
					targets.add(thing);
				}
			}
		}

		if (targets.size === 0) return false;

		for (const t of targets) {
			if (!t.active) continue;
			this.attack(t);
		}

		return true;
	}

	protected override isDamageableObstacle(thing: PhysicalThingWithHp): boolean {
		return isBreakableObstacle(thing) || thing instanceof Mob;
	}

	private isAreaDiggable(xy: XY): boolean {
		const things = this.getBoard().getThingsAt(xy);
		const blocking = things.filter((t) => t.blocksMovement);
		return blocking.every((t) => t instanceof PhysicalThingWithHp && this.isDamageableObstacle(t));
	}
}
